import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Plus, Trash2, Package, Mail, Phone, UserRound, Pencil, Check, X } from "lucide-react";

interface MaterialContact {
  id: string;
  company_name: string;
  email: string;
  phone: string | null;
  contact_person: string | null;
  notes: string | null;
}

interface Props {
  supplierId: string;
}

export function MaterialContactsManager({ supplierId }: Props) {
  const [contacts, setContacts] = useState<MaterialContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState({ company_name: "", email: "", phone: "", contact_person: "", notes: "" });
  const [newContact, setNewContact] = useState({ company_name: "", email: "", phone: "", contact_person: "", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadContacts();
  }, [supplierId]);

  const loadContacts = async () => {
    const { data, error } = await supabase
      .from("supplier_material_contacts")
      .select("id, company_name, email, phone, contact_person, notes")
      .eq("supplier_id", supplierId)
      .order("created_at", { ascending: true });

    if (data) setContacts(data);
    if (error) console.error("Failed to load material contacts:", error);
    setLoading(false);
  };

  const addContact = async () => {
    if (!newContact.company_name.trim() || !newContact.email.trim()) {
      toast.error("Vyplňte název firmy a e-mail");
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("supplier_material_contacts")
      .insert({
        supplier_id: supplierId,
        company_name: newContact.company_name.trim(),
        email: newContact.email.trim().toLowerCase(),
        phone: newContact.phone.trim() || null,
        contact_person: newContact.contact_person.trim() || null,
        notes: newContact.notes.trim() || null,
      });

    if (error) {
      toast.error("Nepodařilo se přidat kontakt");
      console.error(error);
    } else {
      toast.success("Kontakt přidán");
      setNewContact({ company_name: "", email: "", phone: "", contact_person: "", notes: "" });
      setAdding(false);
      loadContacts();
    }
    setSaving(false);
  };

  const deleteContact = async (id: string) => {
    const { error } = await supabase
      .from("supplier_material_contacts")
      .delete()
      .eq("id", id);

    if (error) {
      toast.error("Nepodařilo se smazat kontakt");
    } else {
      setContacts(prev => prev.filter(c => c.id !== id));
      toast.success("Kontakt smazán");
    }
  };

  const startEdit = (contact: MaterialContact) => {
    setEditingId(contact.id);
    setEditData({
      company_name: contact.company_name,
      email: contact.email,
      phone: contact.phone || "",
      contact_person: contact.contact_person || "",
      notes: contact.notes || "",
    });
  };

  const saveEdit = async () => {
    if (!editingId || !editData.company_name.trim() || !editData.email.trim()) {
      toast.error("Vyplňte název firmy a e-mail");
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("supplier_material_contacts")
      .update({
        company_name: editData.company_name.trim(),
        email: editData.email.trim().toLowerCase(),
        phone: editData.phone.trim() || null,
        contact_person: editData.contact_person.trim() || null,
        notes: editData.notes.trim() || null,
      })
      .eq("id", editingId);

    if (error) {
      toast.error("Nepodařilo se uložit změny");
    } else {
      toast.success("Kontakt upraven");
      setEditingId(null);
      loadContacts();
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="animate-pulse h-16 bg-muted/30 rounded-lg" />;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-foreground flex items-center gap-1.5">
          <Package className="w-3.5 h-3.5 text-primary" />
          Dodavatelé materiálu
        </h4>
        <button
          onClick={() => setAdding(!adding)}
          className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors font-medium"
        >
          <Plus className="w-3.5 h-3.5" />
          Přidat
        </button>
      </div>

      <p className="text-[11px] text-muted-foreground -mt-1">
        Firmy, kterým posíláte poptávky na materiál (DEK, PRO-DOMA, výrobci apod.)
      </p>

      {/* Existing contacts */}
      {contacts.length > 0 && (
        <div className="space-y-2">
          {contacts.map(contact => (
            editingId === contact.id ? (
              <div key={contact.id} className="border border-primary/30 rounded-lg p-3 bg-primary/5 space-y-2.5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] text-muted-foreground mb-0.5 block">Název firmy *</label>
                    <input type="text" value={editData.company_name} onChange={(e) => setEditData({ ...editData, company_name: e.target.value })} className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground" />
                  </div>
                  <div>
                    <label className="text-[11px] text-muted-foreground mb-0.5 block">E-mail *</label>
                    <input type="email" value={editData.email} onChange={(e) => setEditData({ ...editData, email: e.target.value })} className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground" />
                  </div>
                  <div>
                    <label className="text-[11px] text-muted-foreground mb-0.5 block">Telefon</label>
                    <input type="tel" value={editData.phone} onChange={(e) => setEditData({ ...editData, phone: e.target.value })} className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground" />
                  </div>
                  <div>
                    <label className="text-[11px] text-muted-foreground mb-0.5 block">Jméno obchodníka</label>
                    <input type="text" value={editData.contact_person} onChange={(e) => setEditData({ ...editData, contact_person: e.target.value })} className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground" />
                  </div>
                  <div>
                    <label className="text-[11px] text-muted-foreground mb-0.5 block">Poznámka</label>
                    <input type="text" value={editData.notes} onChange={(e) => setEditData({ ...editData, notes: e.target.value })} className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={saveEdit} disabled={saving || !editData.company_name.trim() || !editData.email.trim()} className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 text-xs font-medium">
                    <Check className="w-3.5 h-3.5" />
                    {saving ? "Ukládám..." : "Uložit"}
                  </button>
                  <button onClick={() => setEditingId(null)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-muted-foreground hover:bg-accent rounded-lg transition-colors">
                    <X className="w-3.5 h-3.5" />
                    Zrušit
                  </button>
                  <button onClick={() => { deleteContact(contact.id); setEditingId(null); }} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-destructive hover:bg-destructive/10 rounded-lg transition-colors ml-auto">
                    <Trash2 className="w-3.5 h-3.5" />
                    Smazat
                  </button>
                </div>
              </div>
            ) : (
              <div
                key={contact.id}
                onClick={() => startEdit(contact)}
                className="flex items-center gap-3 border border-border rounded-lg px-3 py-2.5 bg-background group cursor-pointer hover:border-primary/30 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{contact.company_name}</p>
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                    <span className="flex items-center gap-1 text-xs text-muted-foreground truncate">
                      <Mail className="w-3 h-3 shrink-0" />
                      {contact.email}
                    </span>
                    {contact.contact_person && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <UserRound className="w-3 h-3 shrink-0" />
                        {contact.contact_person}
                      </span>
                    )}
                    {contact.phone && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Phone className="w-3 h-3 shrink-0" />
                        {contact.phone}
                      </span>
                    )}
                  </div>
                  {contact.notes && (
                    <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{contact.notes}</p>
                  )}
                </div>
                <Pencil className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
              </div>
            )
          ))}
        </div>
      )}

      {contacts.length === 0 && !adding && (
        <p className="text-xs text-muted-foreground italic py-2">
          Zatím nemáte žádné dodavatele materiálu. Přidejte je pro rychlé poptávky.
        </p>
      )}

      {/* Add new contact form */}
      {adding && (
        <div className="border border-primary/30 rounded-lg p-3 bg-primary/5 space-y-2.5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] text-muted-foreground mb-0.5 block">Název firmy *</label>
              <input
                type="text"
                value={newContact.company_name}
                onChange={(e) => setNewContact({ ...newContact, company_name: e.target.value })}
                placeholder="např. DEK a.s."
                className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground"
              />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground mb-0.5 block">E-mail *</label>
              <input
                type="email"
                value={newContact.email}
                onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                placeholder="obchod@dek.cz"
                className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground"
              />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground mb-0.5 block">Telefon</label>
              <input
                type="tel"
                value={newContact.phone}
                onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                placeholder="+420..."
                className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground"
              />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground mb-0.5 block">Jméno obchodníka</label>
              <input
                type="text"
                value={newContact.contact_person}
                onChange={(e) => setNewContact({ ...newContact, contact_person: e.target.value })}
                placeholder="např. Jan Novák"
                className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground"
              />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground mb-0.5 block">Poznámka</label>
              <input
                type="text"
                value={newContact.notes}
                onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
                placeholder="např. pobočka Praha"
                className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={addContact}
              disabled={saving || !newContact.company_name.trim() || !newContact.email.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 text-xs font-medium"
            >
              {saving ? "Ukládám..." : "Přidat kontakt"}
            </button>
            <button
              onClick={() => { setAdding(false); setNewContact({ company_name: "", email: "", phone: "", contact_person: "", notes: "" }); }}
              className="px-3 py-1.5 text-xs text-muted-foreground hover:bg-accent rounded-lg transition-colors"
            >
              Zrušit
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
